import React, { useState, useEffect } from 'react';
import useFetch from '../../hooks/useFetch';

function UserReports() {
  const [reports, setReports] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [liveOnly, setLiveOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ bySeason: [], byYear: [] });
  const { fetchData, loading, error } = useFetch();

  // Fetch data whenever the selectedYear changes
  useEffect(() => {
    const fetchReports = async () => {
      // Construct URL with query param if a year is selected
      let url = `/api/admin/user-reports?page=${currentPage}&limit=20`;
      if (selectedYear) {
        url += `&year=${selectedYear}`;
      }
      if (liveOnly) {
        url += '&liveOnly=true';
      }

      const data = await fetchData(url);

      if (data) {
        // We expect the API to return { reports: [...], availableYears: [...] }
        setReports(data.reports);
        if (data.pagination) {
          setTotalPages(data.pagination.totalPages);
        }
        if (data.stats) {
          setStats(data.stats);
        }

        // Only set available years if we haven't yet (or if the API returns them every time)
        if (data.availableYears && availableYears.length === 0) {
          setAvailableYears(data.availableYears);
        }
      }
    };

    fetchReports();
    // Dependency array: re-run when selectedYear changes
  }, [selectedYear, liveOnly, currentPage, availableYears, fetchData]);

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setCurrentPage(1);
  };

  const handleLiveOnlyChange = (e) => {
    setLiveOnly(e.target.checked);
    setCurrentPage(1); // Reset to page 1 when filter changes
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="container mx-auto p-4 text-white">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Racer Data Reports</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-gray-700 p-4 rounded-lg shadow-md">
          <h3 className="text-xl font-bold mb-3 border-b border-gray-600 pb-2">Unique Users by Year</h3>
          <div className="max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500">
            <table className="w-full text-left">
              <thead>
                <tr className="text-gray-400 text-sm">
                  <th className="pb-2">Year</th>
                  <th className="pb-2 text-right">Users</th>
                </tr>
              </thead>
              <tbody>
                {stats.byYear.map((stat) => (
                  <tr key={stat.year} className="border-b border-gray-600 last:border-0 hover:bg-gray-600">
                    <td className="py-2">{stat.year}</td>
                    <td className="py-2 pr-2 text-right">{stat.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-gray-700 p-4 rounded-lg shadow-md">
          <h3 className="text-xl font-bold mb-3 border-b border-gray-600 pb-2">Unique Users by Season</h3>
          <div className="max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500">
            <table className="w-full text-left">
              <thead>
                <tr className="text-gray-400 text-sm">
                  <th className="pb-2">Season</th>
                  <th className="pb-2 pr-2 text-right">Users</th>
                </tr>
              </thead>
              <tbody>
                {stats.bySeason.map((stat) => (
                  <tr key={stat.season} className="border-b border-gray-600 last:border-0 hover:bg-gray-600">
                    <td className="py-2">{stat.season}</td>
                    <td className="py-2 pr-2 text-right">{stat.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center mb-6">

        <div className="flex items-center">
          <label htmlFor="year-select" className="font-medium">
            Filter by Year:
            <select
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              disabled={loading}
              className="ml-2 p-2 border rounded bg-gray-700 text-white border-gray-500 focus:outline-none focus:border-blue-500"
            >
              <option value="">All Time</option>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>

          <label htmlFor="live-only" className="font-medium ml-6 flex items-center cursor-pointer">
            <input
              id="live-only"
              type="checkbox"
              checked={liveOnly}
              onChange={handleLiveOnlyChange}
              className="mr-2 h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
            />
            Show Live Only
          </label>
        </div>
      </div>

      {error && <div className="bg-red-500 text-white p-4 rounded mb-4">Error: {error.message || error}</div>}

      {loading ? (
        <div className="text-center py-8 text-xl">Loading data...</div>
      ) : (
        <div className="overflow-x-auto bg-gray-600 rounded-lg shadow-lg border border-gray-500">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-700 text-gray-200">
              <tr>
                <th className="py-3 px-4 font-semibold border-b border-gray-500">Channel Name</th>
                {liveOnly && <th className="py-3 px-4 font-semibold border-b border-gray-500">Game</th>}
                <th className="py-3 px-4 font-semibold border-b border-gray-500">Last Updated</th>
              </tr>
            </thead>
            <tbody className="text-white">
              {reports.length > 0 ? (
                reports.map((report) => (
                  <tr key={report.channel} className="hover:bg-gray-500 border-b border-gray-500 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <a
                          href={`https://twitch.tv/${report.channel}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center hover:text-blue-400 transition-colors"
                        >
                          {report.profile_image_url && (
                            <img src={report.profile_image_url} alt="" className="w-8 h-8 rounded-full mr-3" />
                          )}
                          <span className="font-medium">{report.channel}</span>
                        </a>
                        {report.isLive && (
                          <span className="ml-3 px-2 py-0.5 text-xs font-bold rounded bg-red-600 text-white animate-pulse">LIVE</span>
                        )}
                      </div>
                    </td>
                    {liveOnly && (
                      <td className="py-3 px-4">{report.gameName || 'Unknown'}</td>
                    )}
                    <td className="py-3 px-4">
                      {/* Format date to local string */}
                      {new Date(report.last_updated).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="2" className="py-8 text-center text-gray-300 italic">
                    No updates found for this time period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && reports.length > 0 && !liveOnly && (
        <div className="flex justify-between items-center mt-4">
          <button
            type="button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            className="bg-gray-700 text-white px-4 py-2 rounded disabled:opacity-50 hover:bg-gray-600"
          >
            Previous
          </button>
          <span className="text-white">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
            className="bg-gray-700 text-white px-4 py-2 rounded disabled:opacity-50 hover:bg-gray-600"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default UserReports;
